import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  CommandReceipt,
  Role,
  WorkspaceCommand,
  WorkspaceState,
} from "../domain/types";
import { applyCommand } from "../domain/engine";
import {
  ApiError,
  createGuardianLink,
  restoreSession,
  sendCommand,
  startSample,
} from "../lib/api";

interface WorkspaceContextValue {
  state: WorkspaceState | null;
  role: Role | "sample_user" | null;
  mode: "sample" | "user" | null;
  loading: boolean;
  mutating: boolean;
  online: boolean;
  notice: string | null;
  error: ApiError | null;
  lastReceipt: CommandReceipt | null;
  offlineConflicts: OfflineConflict[];
  beginSample: () => Promise<WorkspaceState>;
  command: (command: WorkspaceCommand) => Promise<CommandReceipt>;
  makeGuardianLink: (
    draftId: string,
  ) => Promise<{ url: string; expiresAt: string; remainingUses: number }>;
  refresh: () => Promise<void>;
  resolveOfflineConflict: (
    conflictId: string,
    choice: "server" | "local",
  ) => Promise<void>;
  adoptSession: (next: {
    state: WorkspaceState;
    csrfToken: string;
    role: Role;
    mode?: "user";
  }) => void;
  clearNotice: () => void;
}

interface OfflineQueueItem {
  id: string;
  command: WorkspaceCommand;
  baseVersion: number;
  queuedAt: string;
}

export interface OfflineConflict {
  id: string;
  command: Extract<WorkspaceCommand, { type: "attendance.mark" }>;
  serverValue: string;
  localValue: string;
}

const OFFLINE_QUEUE_KEY = "sueopmachim:offline-queue:v1";

function readOfflineQueue(): OfflineQueueItem[] {
  try {
    const value = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return value ? (JSON.parse(value) as OfflineQueueItem[]) : [];
  } catch {
    return [];
  }
}

function writeOfflineQueue(items: OfflineQueueItem[]): void {
  if (items.length)
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(items));
  else localStorage.removeItem(OFFLINE_QUEUE_KEY);
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WorkspaceState | null>(null);
  const [role, setRole] = useState<Role | "sample_user" | null>(null);
  const [mode, setMode] = useState<"sample" | "user" | null>(null);
  const [csrfToken, setCsrfToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [lastReceipt, setLastReceipt] = useState<CommandReceipt | null>(null);
  const [offlineConflicts, setOfflineConflicts] = useState<OfflineConflict[]>(
    [],
  );

  const refresh = useCallback(async () => {
    try {
      const session = await restoreSession();
      setState(session.state);
      setCsrfToken(session.csrfToken);
      setRole(session.role);
      setMode(session.mode);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status !== 401) setError(caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setNotice("연결이 돌아왔어요. 보존된 입력을 다시 저장할 수 있습니다.");
    };
    const onOffline = () => {
      setOnline(false);
      setNotice(
        "오프라인입니다. 아직 보내지 않은 입력은 이 기기에 보존합니다.",
      );
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const beginSample = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sample = await startSample();
      setState(sample.state);
      setCsrfToken(sample.csrfToken);
      setRole("sample_user");
      setMode("sample");
      setNotice(
        "합성 데이터로 만든 샘플 하루예요. 실제 학생 정보는 포함되지 않습니다.",
      );
      return sample.state;
    } catch (caught) {
      const apiError =
        caught instanceof ApiError
          ? caught
          : new ApiError(
              "UNKNOWN",
              "샘플을 열지 못했어요.",
              500,
              true,
              "unknown",
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  const queueOptimistic = useCallback(
    (
      current: WorkspaceState,
      nextCommand: WorkspaceCommand,
      operationId: string,
    ): CommandReceipt => {
      const queuedAt = new Date().toISOString();
      const queue = readOfflineQueue();
      queue.push({
        id: operationId,
        command: nextCommand,
        baseVersion: current.version,
        queuedAt,
      });
      writeOfflineQueue(queue);
      const optimistic = applyCommand(current, nextCommand, {
        now: queuedAt,
        actorId: "offline-device",
        actorRole: role ?? "read_only",
        scopeClassIds:
          role === "instructor" ? current.classes.map((item) => item.id) : null,
        eventId: operationId,
        idempotencyKey: operationId,
      });
      if (nextCommand.type === "attendance.mark") {
        const record = optimistic.state.attendance.find(
          (item) =>
            item.sessionId === nextCommand.sessionId &&
            item.studentId === nextCommand.studentId,
        );
        if (record) {
          record.syncState = "pending";
          record.source = "offline_queue";
        }
      }
      setState(optimistic.state);
      setLastReceipt(optimistic.receipt);
      setNotice(
        `${optimistic.receipt.summary} 서버에 닿으면 같은 저장 키로 확인합니다.`,
      );
      return optimistic.receipt;
    },
    [role],
  );

  const runCommand = useCallback(
    async (nextCommand: WorkspaceCommand) => {
      if (!state || !csrfToken)
        throw new ApiError(
          "SESSION_REQUIRED",
          "먼저 샘플을 열거나 로그인해 주세요.",
          401,
          false,
          "client",
        );
      setMutating(true);
      setError(null);
      const operationId = `web-${crypto.randomUUID()}`;
      if (!online) {
        try {
          return queueOptimistic(state, nextCommand, operationId);
        } finally {
          setMutating(false);
        }
      }
      try {
        const result = await sendCommand(
          state,
          csrfToken,
          nextCommand,
          operationId,
        );
        setState(result.state);
        setLastReceipt(result.receipt);
        setNotice(result.receipt.summary);
        return result.receipt;
      } catch (caught) {
        const apiError =
          caught instanceof ApiError
            ? caught
            : new ApiError(
                "UNKNOWN",
                "저장하지 못했어요. 입력은 유지됩니다.",
                500,
                true,
                "unknown",
              );
        if (apiError.code === "NETWORK_ERROR" || apiError.status === 0) {
          return queueOptimistic(state, nextCommand, operationId);
        }
        setError(apiError);
        if (apiError.code === "VERSION_CONFLICT") await refresh();
        throw apiError;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken, online, queueOptimistic, refresh, state],
  );

  const flushOfflineQueue = useCallback(async () => {
    const queued = readOfflineQueue();
    if (!queued.length) return;
    setMutating(true);
    try {
      const session = await restoreSession();
      let latest = session.state;
      let completed = 0;
      setCsrfToken(session.csrfToken);
      setRole(session.role);
      setMode(session.mode);
      for (let index = 0; index < queued.length; index += 1) {
        const item = queued[index]!;
        try {
          const result = await sendCommand(
            { ...latest, version: item.baseVersion },
            session.csrfToken,
            item.command,
            item.id,
          );
          latest = result.state;
          completed += 1;
          writeOfflineQueue(queued.slice(index + 1));
          setLastReceipt(result.receipt);
        } catch (caught) {
          const apiError =
            caught instanceof ApiError
              ? caught
              : new ApiError(
                  "UNKNOWN",
                  "대기 기록을 동기화하지 못했어요.",
                  500,
                  true,
                  "offline-sync",
                );
          if (
            apiError.code === "VERSION_CONFLICT" &&
            item.command.type === "attendance.mark"
          ) {
            const attendanceCommand = item.command;
            const serverRecord = latest.attendance.find(
              (record) =>
                record.sessionId === attendanceCommand.sessionId &&
                record.studentId === attendanceCommand.studentId,
            );
            if (serverRecord) serverRecord.syncState = "conflict";
            setOfflineConflicts((current) => [
              ...current.filter((conflict) => conflict.id !== item.id),
              {
                id: item.id,
                command: attendanceCommand,
                serverValue: serverRecord?.status ?? "unmarked",
                localValue: attendanceCommand.status,
              },
            ]);
            writeOfflineQueue(queued.slice(index + 1));
            setNotice(
              "같은 학생의 출결이 다른 기기에서 바뀌었어요. 두 값을 보고 선택해 주세요.",
            );
          } else {
            writeOfflineQueue(queued.slice(index));
            setError(apiError);
          }
          break;
        }
      }
      setState(latest);
      if (completed > 0 && readOfflineQueue().length === 0) {
        setNotice(`기기에 보존한 ${completed}건을 서버 영수증으로 확인했어요.`);
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status !== 401) setError(caught);
    } finally {
      setMutating(false);
    }
  }, []);

  useEffect(() => {
    if (online) void flushOfflineQueue();
  }, [flushOfflineQueue, online]);

  const resolveOfflineConflict = useCallback(
    async (conflictId: string, choice: "server" | "local") => {
      const conflict = offlineConflicts.find((item) => item.id === conflictId);
      if (!conflict) return;
      if (choice === "local") await runCommand(conflict.command);
      else {
        setState((current) => {
          if (!current) return current;
          const next = structuredClone(current);
          const record = next.attendance.find(
            (item) =>
              item.sessionId === conflict.command.sessionId &&
              item.studentId === conflict.command.studentId,
          );
          if (record) record.syncState = "synced";
          return next;
        });
        setNotice("서버에 먼저 저장된 출결을 유지했어요.");
      }
      setOfflineConflicts((current) =>
        current.filter((item) => item.id !== conflictId),
      );
    },
    [offlineConflicts, runCommand],
  );

  const makeGuardianLink = useCallback(
    async (draftId: string) => {
      if (!state || !csrfToken)
        throw new ApiError(
          "SESSION_REQUIRED",
          "먼저 샘플을 열거나 로그인해 주세요.",
          401,
          false,
          "client",
        );
      setMutating(true);
      setError(null);
      try {
        const result = await createGuardianLink(state, csrfToken, draftId);
        setState(result.state);
        setLastReceipt(result.receipt);
        setNotice(result.receipt.summary);
        return {
          url: result.url,
          expiresAt: result.expiresAt,
          remainingUses: result.remainingUses,
        };
      } catch (caught) {
        const apiError =
          caught instanceof ApiError
            ? caught
            : new ApiError(
                "UNKNOWN",
                "보호자 링크를 만들지 못했어요.",
                500,
                true,
                "unknown",
              );
        setError(apiError);
        if (apiError.code === "VERSION_CONFLICT") await refresh();
        throw apiError;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken, refresh, state],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      state,
      role,
      mode,
      loading,
      mutating,
      online,
      notice,
      error,
      lastReceipt,
      offlineConflicts,
      beginSample,
      command: runCommand,
      makeGuardianLink,
      refresh,
      resolveOfflineConflict,
      adoptSession(next) {
        setState(next.state);
        setCsrfToken(next.csrfToken);
        setRole(next.role);
        setMode(next.mode ?? "user");
        setError(null);
      },
      clearNotice: () => {
        setNotice(null);
        setError(null);
      },
    }),
    [
      beginSample,
      error,
      lastReceipt,
      loading,
      makeGuardianLink,
      mode,
      mutating,
      notice,
      offlineConflicts,
      online,
      refresh,
      resolveOfflineConflict,
      role,
      runCommand,
      state,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("WorkspaceProvider is missing");
  return value;
}
